import React, { ButtonHTMLAttributes } from 'react';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isLoading?: boolean;
  animated?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  isLoading = false,
  animated = false,
  disabled = false,
  ...props
}) => {
  const baseClasses = 'btn flex items-center justify-center rounded-lg font-medium transition-all duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50';

  const variantClasses = {
    primary: 'btn-primary shadow-lg shadow-[#7F56D9]/20 hover:shadow-xl hover:shadow-[#7F56D9]/30 hover:-translate-y-0.5 focus:ring-[#7F56D9]',
    secondary: 'btn-secondary shadow-lg shadow-[#F670C7]/20 hover:shadow-xl hover:shadow-[#F670C7]/30 hover:-translate-y-0.5 focus:ring-[#F670C7]',
    outline: 'border border-gray-600 text-white hover:bg-[#2A2633] hover:border-gray-500 hover:-translate-y-0.5 focus:ring-gray-500',
    ghost: 'text-white hover:bg-white/10 hover:-translate-y-0.5 focus:ring-white',
    danger: 'bg-[#F04438] text-white shadow-lg shadow-[#F04438]/20 hover:bg-[#E42D20] hover:shadow-xl hover:shadow-[#F04438]/30 hover:-translate-y-0.5 focus:ring-[#F04438]',
    success: 'bg-[#12B76A] text-white shadow-lg shadow-[#12B76A]/20 hover:bg-[#0E9F5D] hover:shadow-xl hover:shadow-[#12B76A]/30 hover:-translate-y-0.5 focus:ring-[#12B76A]'
  };

  const sizeClasses = {
    xs: 'text-xs py-1 px-2 gap-1',
    sm: 'text-xs py-1.5 px-3 gap-1.5',
    md: 'text-sm py-2.5 px-5 gap-2',
    lg: 'text-base py-3.5 px-7 gap-2.5',
    xl: 'text-lg py-4 px-8 gap-3'
  };

  const widthClass = fullWidth ? 'w-full' : '';
  const animationClass = animated ? 'animate-pulse' : '';
  const disabledClass = disabled || isLoading ? 'opacity-70 cursor-not-allowed' : '';

  const combinedClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${widthClass} ${animationClass} ${disabledClass} ${className}`;

  return (
    <button
      className={combinedClasses}
      disabled={disabled || isLoading}
      {...props}
    >
      {isLoading ? (
        <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      ) : leftIcon ? (
        <span className="inline-flex">{leftIcon}</span>
      ) : null}
      {children}
      {rightIcon && !isLoading && <span className="inline-flex">{rightIcon}</span>}
    </button>
  );
};

export default Button;
